/**
 * @file frontend/src/components/ui/badge.jsx
 * @module ui
 * @component badge
 *
 * Badge — Shadcn/UI primitive component.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import * as React from "react"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  ...props
}) {
  return (<div className={cn(badgeVariants({ variant }), className)} {...props} />);
}

export { Badge, badgeVariants }
